import { useCallback, useEffect, useRef, useState, type JSX } from 'react';
import type { StepSchema, RunState, ExtendedCertificate } from 'shared';
import { resolveMediaRefToUrl } from '../../utils/mediaRef';
import { DetailedReport } from '../certificate';
import { recordTsukumogamiWithSync } from '../../utils/collectionSync';
import { endingGlyph } from '../../utils/endingGlyph';
import { TsukumogamiCollection } from '../collection/TsukumogamiCollection';
import { audioEngine } from '../../audio/audioEngine';
import { readDomMood } from '../../minigames/foundation/hooks/readDomMood';

type EndingStep = Extract<StepSchema, { type: 'ending' }>;

export interface EndingViewProps {
  step: EndingStep;
  runState?: RunState | null;
  onAction?: (action: 'replay' | 'home' | 'url', url?: string) => void;
  onGenerateCertificate?: () => Promise<ExtendedCertificate>;
  /** エンディング到達時にランを完了としてマークするコールバック */
  onCompleteEnding?: (endingId?: string) => Promise<void>;
  /** P9-1: このプレイスルーを一意に識別する id。図鑑記録の冪等キー（recordedRunIds）に使う。 */
  playthroughId?: string;
  /** 図鑑を現在の物語へ絞るためのシナリオ id。 */
  scenarioId?: string;
}

type AnimationPhase = 'image' | 'title' | 'message' | 'stats' | 'buttons';

export function EndingView({ step, runState, onAction, onGenerateCertificate, onCompleteEnding, playthroughId, scenarioId }: EndingViewProps): JSX.Element {
  const [phase, setPhase] = useState<AnimationPhase>('image');
  const [certificate, setCertificate] = useState<ExtendedCertificate | null>(null);
  const [isCertificateLoading, setIsCertificateLoading] = useState(false);
  const [showCertificate, setShowCertificate] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  // 没入 P5: 付喪神図鑑モーダルの開閉（EndingView が own する＝App.tsx の phase に干渉しない）。
  const [showCollection, setShowCollection] = useState(false);
  const animationMs = step.animationDurationMs ?? 1500;
  const animationType = step.animation ?? 'fade';

  // P8-1 継ぎ目の儀式: ending 到達のスティング（1 回だけ・StrictMode 二重 mount ガード）。
  // 既存 AnimationPhase の起点（'image'＝マウント直後）に同期する。mute 中は engine が無視。
  const endingStingRef = useRef(false);
  useEffect(() => {
    if (endingStingRef.current) return;
    endingStingRef.current = true;
    const mood = readDomMood();
    audioEngine.playSfx('reveal', { mood, volume: 0.55 });
  }, []);

  // P8-1: 到達で ambient（合成ドローン）を一段静める→余韻。離脱（replay/home）で戻す。
  // mood/ドローン構成は変えず gain 係数のみ（setAmbientDuck）。
  useEffect(() => {
    audioEngine.setAmbientDuck(0.4);
    return () => audioEngine.setAmbientDuck(1);
  }, []);

  // エンディング到達時に自動的にランを完了としてマーク
  useEffect(() => {
    if (onCompleteEnding && !isCompleted) {
      onCompleteEnding(step.endingId).then(() => {
        setIsCompleted(true);
      }).catch((err) => {
        console.warn('Failed to complete ending:', err);
        // エラーでも続行可能にする（ローカルモードなど）
        setIsCompleted(true);
      });
    }
  }, [onCompleteEnding, step.endingId, isCompleted]);

  // P9-1: StrictMode の二重 useEffect / 再レンダーによる二重呼び出しを、呼び出し側でも遮断する
  // ref ガード（step.id 単位）。冪等の本丸は collection.ts の recordedRunIds（runId 単位の完全 no-op）
  // であり、これは「そもそも二重に呼ばない」ための多重防御。
  const recordedStepIdRef = useRef<string | null>(null);

  // 没入 P5「声を取り戻した付喪神図鑑」: エンディング到達でこの付喪神を端末コレクションへ記録する。
  // step.tsukumogamiData が無いエンディング（従来シナリオ）は何もしない。upsert なので周回は clearCount++。
  // EndingView は scenarioId/title を props に持たないため、由来表示には endingTitle を scenarioTitle として渡す
  // （soma では endingTitle = 「杣川運動公園の呼び火」＝シナリオ表示名と一致）。key は付喪神 name に集約される。
  // P9-1: runId（playthroughId）を冪等キーとして渡す。同一プレイスルーで ending を開き直しても
  // clearCount は増えない（新規プレイスルーは App 層で id が再採番されるため周回は加算される）。
  useEffect(() => {
    const data = step.tsukumogamiData;
    if (!data) return;
    if (recordedStepIdRef.current === step.id) return;
    recordedStepIdRef.current = step.id;
    // P9-2: 記録がカウントされた場合のみ、同期層がサーバ台帳への送信キュー（outbox）へ積んで
    // flush を試みる。Worker 未対応・オフラインでもローカル記録は従来どおり即時（graceful degradation）。
    recordTsukumogamiWithSync({
      name: data.name,
      description: data.description,
      illustrationAssetId: data.illustrationAssetId,
      rarity: data.rarity,
      scenarioTitle: step.endingTitle,
      endingType: step.endingType,
      runId: playthroughId
    });
    // P8-1: 図鑑への記録が発生した瞬間の小さな「記帳」音（1 回・控えめ）。
    audioEngine.playSfx('inscribe', { mood: readDomMood(), volume: 0.5 });
    // step 単位で 1 回（マウント時）。同一エンディング step が再マウントされたら再 upsert を試みる
    // （周回想定。同一 runId なら collection 側で no-op）。
  }, [step.tsukumogamiData, step.endingTitle, step.endingType, step.id, playthroughId]);

  useEffect(() => {
    if (animationType === 'none') {
      setPhase('buttons');
      return;
    }

    const phaseDelays: { phase: AnimationPhase; delay: number }[] = [
      { phase: 'title', delay: animationMs * 0.6 },
      { phase: 'message', delay: animationMs * 1.2 },
      { phase: 'stats', delay: animationMs * 1.8 },
      { phase: 'buttons', delay: animationMs * 2.4 }
    ];

    const timers = phaseDelays.map(({ phase: p, delay }) =>
      setTimeout(() => setPhase(p), delay)
    );

    return () => timers.forEach(clearTimeout);
  }, [animationType, animationMs]);

  const handleButtonClick = (action: 'replay' | 'home' | 'url', url?: string) => {
    onAction?.(action, url);
  };

  const handleViewCertificate = async () => {
    if (certificate) {
      setShowCertificate(true);
      return;
    }

    if (!onGenerateCertificate) return;

    setIsCertificateLoading(true);
    try {
      const cert = await onGenerateCertificate();
      setCertificate(cert);
      setShowCertificate(true);
    } catch (error) {
      console.error('Failed to generate certificate', error);
    } finally {
      setIsCertificateLoading(false);
    }
  };

  const getPhaseIndex = (p: AnimationPhase): number => {
    const order: AnimationPhase[] = ['image', 'title', 'message', 'stats', 'buttons'];
    return order.indexOf(p);
  };

  const isVisible = (targetPhase: AnimationPhase): boolean => {
    return getPhaseIndex(phase) >= getPhaseIndex(targetPhase);
  };

  const fadeClass = (targetPhase: AnimationPhase): string => {
    if (animationType === 'none') return 'ending-visible';
    return isVisible(targetPhase) ? 'ending-fade-in' : 'ending-hidden';
  };

  const slideClass = (targetPhase: AnimationPhase): string => {
    if (animationType === 'none') return 'ending-visible';
    return isVisible(targetPhase) ? 'ending-slide-in' : 'ending-hidden';
  };

  const animClass = animationType === 'slide' ? slideClass : fadeClass;

  const imageUrl = step.imageAssetId ? resolveMediaRefToUrl(step.imageAssetId) : null;

  // 達成情報の計算
  const stats = computeStats(step, runState);

  // P8-3「シェルの声」: 英語バッジ（GOOD END 等）を廃し、図鑑チップと同一の
  // 世界内一字表記（真/吉/常/凶/秘/戯・SSoT: utils/endingGlyph.ts）を使う。
  // P8-2b: endingType 別の Web 色（#22c55e 等）も廃止。バッジは spot_gate 印章「合」
  // （P8-1）と同じ金印章の単一意匠（CSS .ending-view__type-badge）で描く。
  const endingTypeLabel = endingGlyph(step.endingType);

  return (
    <div className="ending-view">
      {/* 背景画像 */}
      {imageUrl && (
        <div className={`ending-view__image ${animClass('image')}`}>
          <img src={imageUrl} alt="" />
          <div className="ending-view__image-overlay" />
        </div>
      )}

      {/* タイトルエリア */}
      <div className={`ending-view__title-area ${animClass('title')}`}>
        {endingTypeLabel && (
          <span className="ending-view__type-badge">
            {endingTypeLabel}
          </span>
        )}
        <h1 className="ending-view__title">{step.endingTitle}</h1>
        {step.endingSubtitle && (
          <p className="ending-view__subtitle">{step.endingSubtitle}</p>
        )}
      </div>

      {/* メッセージ */}
      {step.message && (
        <div className={`ending-view__message ${animClass('message')}`}>
          <p>{step.message}</p>
        </div>
      )}

      {/* 達成情報 */}
      {step.showStats && stats && (
        <div className={`ending-view__stats ${animClass('stats')}`}>
          {stats.playTime && (
            <div className="ending-view__stat-item">
              <span className="ending-view__stat-label">プレイ時間</span>
              <span className="ending-view__stat-value">{stats.playTime}</span>
            </div>
          )}
          {stats.collectedItems && (
            <div className="ending-view__stat-item">
              <span className="ending-view__stat-label">収集アイテム</span>
              <span className="ending-view__stat-value">{stats.collectedItems}</span>
            </div>
          )}
          {stats.correctAnswers && (
            <div className="ending-view__stat-item">
              <span className="ending-view__stat-label">正解率</span>
              <span className="ending-view__stat-value">{stats.correctAnswers}</span>
            </div>
          )}
        </div>
      )}

      {/* ボタンエリア */}
      <div className={`ending-view__buttons ${animClass('buttons')}`}>
        {step.comedyMode?.enabled ? (
          <ComedyEndingButtons
            primary={step.comedyMode.primaryButton}
            secondary={step.comedyMode.secondaryButton}
            extras={step.comedyMode.extras}
            onAction={handleButtonClick}
          />
        ) : (
          <>
            {onGenerateCertificate && (
              <button
                className="ending-view__button ending-view__button--certificate"
                onClick={handleViewCertificate}
                disabled={isCertificateLoading}
              >
                {isCertificateLoading ? '生成中……' : '完了書を見る'}
              </button>
            )}
            {/* 没入 P5: 図鑑導線。記録済みの付喪神コレクションを開く。 */}
            <button
              className="ending-view__button ending-view__button--collection"
              onClick={() => setShowCollection(true)}
            >
              図鑑を見る
            </button>
            {step.showReplayButton && (
              <button
                className="ending-view__button ending-view__button--primary"
                onClick={() => handleButtonClick('replay')}
              >
                最初から
              </button>
            )}
            {step.customButtons?.map((btn, idx) => (
              <button
                key={idx}
                className="ending-view__button"
                onClick={() => handleButtonClick(btn.action, btn.url)}
              >
                {btn.label}
              </button>
            ))}
            {step.showShareButton && (
              <button
                className="ending-view__button ending-view__button--share"
                onClick={() => handleShare(step)}
              >
                共有
              </button>
            )}
          </>
        )}
      </div>

      {/* 解決協力報告書モーダル */}
      {showCertificate && certificate && (
        <DetailedReport
          certificate={certificate}
          onClose={() => setShowCertificate(false)}
        />
      )}

      {/* 没入 P5: 付喪神図鑑モーダル */}
      {showCollection && (
        <TsukumogamiCollection onClose={() => setShowCollection(false)} scenarioId={scenarioId} />
      )}
    </div>
  );
}

function computeStats(
  step: EndingStep,
  runState?: RunState | null
): { playTime?: string; collectedItems?: string; correctAnswers?: string } | null {
  const statsConfig = step.stats;
  if (!statsConfig) {
    return { playTime: formatPlayTime(runState?.stats?.playTimeMs) };
  }

  const result: { playTime?: string; collectedItems?: string; correctAnswers?: string } = {};

  if (statsConfig.showPlayTime) {
    result.playTime = formatPlayTime(runState?.stats?.playTimeMs);
  }

  if (statsConfig.showCollectedItems) {
    const collected = runState?.stats?.collectedItemIds?.length ?? 0;
    const total = runState?.stats?.totalItems ?? 0;
    result.collectedItems = total > 0 ? `${collected}/${total}` : `${collected}`;
  }

  if (statsConfig.showCorrectAnswers) {
    const correct = runState?.stats?.correctAnswers ?? 0;
    const total = runState?.stats?.totalQuestions ?? 0;
    if (total > 0) {
      const rate = Math.round((correct / total) * 100);
      result.correctAnswers = `${rate}%`;
    } else {
      result.correctAnswers = `${correct}`;
    }
  }

  return Object.keys(result).length > 0 ? result : null;
}

function formatPlayTime(ms?: number): string {
  if (!ms || ms <= 0) {
    return '00:00';
  }
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

interface ComedyButtonConfig {
  label?: string;
  action?: 'replay' | 'home' | 'url';
  url?: string;
  sizeFactor?: number;
  animation?: 'none' | 'pulse' | 'bounce' | 'shake';
}

interface ComedySecondaryConfig {
  label?: string;
  action?: 'close' | 'home' | 'url';
  url?: string;
  sizeFactor?: number;
  confirmation?: {
    enabled?: boolean;
    message?: string;
    confirmLabel?: string;
    cancelLabel?: string;
  };
}

interface ComedyExtras {
  runawayButton?: boolean;
  shakeOnConfirm?: boolean;
  extraConfirmMessage?: string;
}

function ComedyEndingButtons({
  primary,
  secondary,
  extras,
  onAction
}: {
  primary?: ComedyButtonConfig;
  secondary?: ComedySecondaryConfig;
  extras?: ComedyExtras;
  onAction?: (action: 'replay' | 'home' | 'url', url?: string) => void;
}) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [showExtraConfirm, setShowExtraConfirm] = useState(false);
  const [buttonOffset, setButtonOffset] = useState({ x: 0, y: 0 });
  const [runawayCount, setRunawayCount] = useState(0);

  const pLabel = primary?.label ?? '再度ゲームを開始する';
  const pAction = primary?.action ?? 'replay';
  const pSizeRaw = primary?.sizeFactor ?? 1.5;
  // モバイル表示を考慮してサイズを制限（最大1.8rem）
  const pSize = Math.min(pSizeRaw, 1.8);
  const pAnim = primary?.animation ?? 'pulse';

  const sLabel = secondary?.label ?? '本当に終了する';
  const sAction = secondary?.action ?? 'close';
  const sSizeRaw = secondary?.sizeFactor ?? 0.5;
  const sSize = Math.min(sSizeRaw, 1.2);
  const confirmation = secondary?.confirmation;

  const handleRunaway = useCallback(() => {
    if (!extras?.runawayButton) return;
    const maxX = window.innerWidth * 0.25;
    const maxY = 60;
    setButtonOffset({
      x: (Math.random() - 0.5) * maxX,
      y: (Math.random() - 0.5) * maxY
    });
    setRunawayCount((c) => c + 1);
  }, [extras?.runawayButton]);

  const handleSecondaryClick = () => {
    // runaway: 2回目のクリックで通す
    if (extras?.runawayButton && runawayCount < 2) {
      handleRunaway();
      return;
    }
    if (confirmation?.enabled) {
      setShowConfirm(true);
    } else {
      onAction?.(sAction === 'close' ? 'home' : sAction, secondary?.url);
    }
  };

  const handleConfirm = () => {
    if (extras?.extraConfirmMessage) {
      setShowExtraConfirm(true);
    } else {
      onAction?.(sAction === 'close' ? 'home' : sAction, secondary?.url);
    }
  };

  return (
    <div className="comedy-ending-buttons">
      <button
        className={`comedy-btn-primary comedy-anim-${pAnim}`}
        style={{
          fontSize: `${pSize}rem`,
          padding: `${pSize * 0.6}rem ${pSize * 1.2}rem`
        }}
        onClick={() => onAction?.(pAction, primary?.url)}
      >
        {pLabel}
      </button>

      <button
        className="comedy-btn-secondary"
        style={{
          fontSize: `${sSize}rem`,
          transform: `translate(${buttonOffset.x}px, ${buttonOffset.y}px)`,
          transition: extras?.runawayButton ? 'transform 0.3s ease-out' : undefined
        }}
        onMouseEnter={extras?.runawayButton ? handleRunaway : undefined}
        onClick={handleSecondaryClick}
      >
        {sLabel}
      </button>

      {showConfirm && !showExtraConfirm && (
        <ComedyConfirmDialog
          message={confirmation?.message ?? '本当に？もうこれ押したら本当に終わるけどいいのね？'}
          confirmLabel={confirmation?.confirmLabel ?? '本当に終了する'}
          cancelLabel={confirmation?.cancelLabel ?? 'やっぱりやめる'}
          shake={extras?.shakeOnConfirm}
          onConfirm={handleConfirm}
          onCancel={() => setShowConfirm(false)}
        />
      )}

      {showExtraConfirm && (
        <ComedyConfirmDialog
          message={extras?.extraConfirmMessage ?? ''}
          confirmLabel="もういい、終わる"
          cancelLabel="やっぱやめる！！"
          onConfirm={() => onAction?.(sAction === 'close' ? 'home' : sAction, secondary?.url)}
          onCancel={() => { setShowExtraConfirm(false); setShowConfirm(false); }}
        />
      )}
    </div>
  );
}

function ComedyConfirmDialog({
  message,
  confirmLabel,
  cancelLabel,
  shake,
  onConfirm,
  onCancel
}: {
  message: string;
  confirmLabel: string;
  cancelLabel: string;
  shake?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className={`comedy-confirm-overlay ${shake ? 'comedy-shake' : ''}`}>
      <div className="comedy-confirm-dialog">
        <p className="comedy-confirm-message">{message}</p>
        <div className="comedy-confirm-actions">
          <button className="comedy-confirm-btn comedy-confirm-btn--cancel" onClick={onCancel}>
            {cancelLabel}
          </button>
          <button className="comedy-confirm-btn comedy-confirm-btn--ok" onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function handleShare(step: EndingStep) {
  const title = step.endingTitle;
  const text = step.endingSubtitle
    ? `${title} - ${step.endingSubtitle}`
    : title;

  if (navigator.share) {
    navigator
      .share({
        title,
        text
      })
      .catch(() => {
        // ユーザーがキャンセルした場合等
      });
  } else {
    // フォールバック: クリップボードにコピー
    navigator.clipboard.writeText(text).catch(() => {
      // ignore
    });
  }
}

export default EndingView;
